/* eslint-disable react/no-array-index-key */
import { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import useEmblaCarousel from 'embla-carousel-react';
import type { BannerFundaciones as BannerFundacionesType } from '@/interfaces/strapi';
import CTNIcon from '@ctn/ui/components/ctn-icon/icon';
import { Section } from '../Section';

type BannerFundacionesProps = {
	readonly className?: string;
	readonly items: BannerFundacionesType[];
	readonly intervalMs?: number;
	readonly hasDots?: boolean;
	readonly hasArrows?: boolean;
};

const classNameComponent = 'c-cnm-banner-fundaciones';

export const BannerFundaciones = ({
	className,
	items,
	intervalMs = 5000,
	hasDots = true,
	hasArrows = true,
}: BannerFundacionesProps) => {
	const wrapperClassName = useMemo(() => classNames(classNameComponent, className), [className]);

	const [viewportRef, embla] = useEmblaCarousel({
		loop: true,
		align: 'start',
		containScroll: 'trimSnaps',
	});

	const [paused, setPaused] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [snapCount, setSnapCount] = useState(0);

	const onSelect = useCallback(() => {
		if (!embla) return;
		setSelectedIndex(embla.selectedScrollSnap());
	}, [embla]);

	useEffect(() => {
		if (!embla) return;
		setSnapCount(embla.scrollSnapList().length);
		onSelect();
		embla.on('select', onSelect);
		embla.on('reInit', onSelect);
		return () => {
			embla?.off?.('select', onSelect);
			embla?.off?.('reInit', onSelect);
		};
	}, [embla, onSelect]);

	useEffect(() => {
		if (!embla || paused || items.length <= 1) return;
		const id = setInterval(() => embla.scrollNext(), intervalMs);
		return () => clearInterval(id);
	}, [embla, paused, intervalMs, items.length]);

	if (!items?.length) return null;

	return (
		<Section
			className={wrapperClassName}
			aria-roledescription="carousel"
			aria-label="Carrusel de fundaciones"
			onMouseEnter={() => setPaused(true)}
			onMouseLeave={() => setPaused(false)}
		>
			{hasArrows && items.length > 1 && (
				<>
					<button
						type="button"
						aria-label="Anterior"
						className={`${classNameComponent}__arrow ${classNameComponent}__arrow--prev`}
						onClick={() => embla?.scrollPrev()}
					>
						<CTNIcon icon="arrowLeft" />
					</button>
					<button
						type="button"
						aria-label="Siguiente"
						className={`${classNameComponent}__arrow ${classNameComponent}__arrow--next`}
						onClick={() => embla?.scrollNext()}
					>
						<CTNIcon icon="arrowRight" />
					</button>
				</>
			)}

			<div ref={viewportRef} className={`${classNameComponent}__viewport`}>
				<div className={`${classNameComponent}__container`}>
					{items.map((item, idx) => (
						<div
							key={idx}
							className={`${classNameComponent}__slide`}
							role="group"
							aria-roledescription="slide"
							aria-label={`Elemento ${idx + 1} de ${items.length}`}
						>
							{/* grid interno del slide */}
							<div className={`${classNameComponent}__hero`}>
								<div className={`${classNameComponent}__imageBox`}>
									<img
										className={`${classNameComponent}__image`}
										src={item.image.url}
										alt={item.title}
										loading="lazy"
										decoding="async"
									/>
								</div>

								<div className={`${classNameComponent}__item-inner`}>
									<h3 className={`${classNameComponent}__title`} title={item.title}>
										{item.title}
									</h3>
									{item.description && (
										<p className={`${classNameComponent}__desc`}>{item.description}</p>
									)}
								</div>
							</div>
						</div>
					))}
				</div>
			</div>

			{hasDots && snapCount > 1 && (
				<div className={`${classNameComponent}__dots`} role="tablist" aria-label="Paginación del carrusel">
					{Array.from({ length: snapCount }).map((_, i) => (
						<button
							key={i}
							type="button"
							className={classNames(`${classNameComponent}__dot`, i === selectedIndex && 'is-active')}
							role="tab"
							aria-selected={i === selectedIndex}
							aria-label={`Ir al elemento ${i + 1}`}
							onClick={() => embla?.scrollTo(i)}
						/>
					))}
				</div>
			)}
		</Section>
	);
};
